import { useState } from 'react';

import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { RiArrowRightSLine } from 'react-icons/ri';

import { IconEnvelopeCloseOutline, Link, SlideIn } from '@/components';
import {
  type CandidateBasicDto,
  type InviteCandidateResponse,
  type MailTemplateDto,
  SendInvitationChannel,
} from '@/services/api';

import { EmailTemplateSelect } from '../../components/EmailTemplateSelect';
import { CircularIconContainer } from '../../components/Icons/IconContainer';
import {
  useCandidatesBulkInviteMutation,
  useInviteAllCandidatesRequest,
} from './useCandidateTableMutation';

interface InviteCandidatesConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId?: string;
  selectedCandidates: Array<CandidateBasicDto>;
}

export const InviteCandidatesConfirmationModal: React.FC<
  InviteCandidatesConfirmationModalProps
> = ({ isOpen, onClose, interviewId, selectedCandidates }) => {
  const toast = useToast();
  const { t } = useTranslation(['common', 'candidate']);
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<MailTemplateDto['id']>();

  const inviteAllCandidates = useInviteAllCandidatesRequest({
    onError() {},
    onSuccess() {
      onClose();
    },
  });

  const candidatesAdd = useCandidatesBulkInviteMutation({
    onError() {},
    onSuccess() {
      onClose();
    },
  });

  const bulkInviteHandler = async () => {
    let mutationPromise: Promise<InviteCandidateResponse>;
    if (interviewId) {
      mutationPromise = candidatesAdd.mutateAsync({
        candidateIds: selectedCandidates.map((c) => c.id),
        interviewId,
        channel: SendInvitationChannel.Email,
        mailTemplateId: selectedTemplateId,
      });
    } else {
      mutationPromise = inviteAllCandidates.mutateAsync({
        body: {
          candidateIds: selectedCandidates.map((c) => c.id),
          mailTemplateId: selectedTemplateId,
          channel: SendInvitationChannel.Email,
        },
      });
    }

    toast.promise(mutationPromise, {
      loading: {
        title: t('candidate:inviteNewCandidate.emailSendingNotification', {
          count: selectedCandidates.length,
        }),
      },
      success: {
        title: t('candidate:inviteNewCandidate.emailSentNotification', {
          count: selectedCandidates.length,
        }),
      },
      error: {
        title: t('common:error.maximumNumberOfEmails'),
      },
    });
  };

  return (
    <Modal
      isCentered
      size="md"
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />

        <VStack gap={0} w="100%" px={6}>
          <ModalHeader justifyItems="center" px={0}>
            <Flex justifyContent="center" mb={3}>
              <CircularIconContainer icon={IconEnvelopeCloseOutline} />
            </Flex>
            <Text mb={1} textAlign="center" fontSize="xl" fontWeight="semibold">
              {t('actions.sendEmail')}
            </Text>
            <Text
              textAlign="center"
              fontSize="sm"
              fontWeight="semibold"
              color="gray.600"
            >
              {t('email.selectTemplate')}
            </Text>
          </ModalHeader>

          <ModalBody w="full" pb={2} px={0}>
            <SlideIn>
              <EmailTemplateSelect
                selectedTemplateId={selectedTemplateId}
                setSelectedTemplateId={setSelectedTemplateId}
              />

              <Button
                onClick={bulkInviteHandler}
                mb={4}
                mt={6}
                isLoading={candidatesAdd.isPending}
                variant="@primary"
                w="full"
              >
                {t('candidate:inviteNewCandidate.emailToCandidates', {
                  count: selectedCandidates.length,
                })}
              </Button>

              <Link to="/admin/settings/emails" mb={5}>
                <Button
                  rightIcon={<RiArrowRightSLine size="20px" />}
                  variant="link"
                  color="primary.500"
                  w="full"
                >
                  {t('email.emailSettings')}
                </Button>
              </Link>
            </SlideIn>
          </ModalBody>
        </VStack>
      </ModalContent>
    </Modal>
  );
};
